// apps/web/src/components/astral/AstralCanvas.tsx
import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import type { MouseEvent as RMouseEvent } from "react";
import { ZoomIn, ZoomOut, Crosshair, HelpCircle, Move, Sparkles } from "lucide-react";
import type { AstralNode, AstralMapData } from "../../types/astral";
import { ASTRAL_THEMES } from "../../data/astralThemes";
import { AstralNodeItem } from "./AstralNodeItem";
import { computeNodesBoundingBox, computeFitViewport } from "./astralViewportUtils";
import { useCanvasTouchGestures } from "./useCanvasTouchGestures";

interface AstralCanvasProps {
  mapData: AstralMapData;
  onChangeMapData?: (newData: AstralMapData) => void;
  readOnly?: boolean;
  autoFitOnMount?: boolean;
}

interface LayoutNodePosition {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  parentId: string | null;
  side: "left" | "right";
}

interface DragState {
  nodeId: string;
  startClientX: number;
  startClientY: number;
  startNodeX: number;
  startNodeY: number;
  currentNodeX: number;
  currentNodeY: number;
  deltaX: number;
  deltaY: number;
  hoverTargetId: string | null;
}

// -------------------------------------------------------------
// ESTIMATIVA DINÂMICA DE DIMENSÕES POR CONTEÚDO E FORMATO DO CARD
// -------------------------------------------------------------
function estimateNodeDimensions(node: AstralNode, isRoot: boolean): { width: number; height: number } {
  const width = isRoot ? 280 : 225;
  const text = node.text || "";
  const hasBadge = Boolean(node.badge);
  // Para nó raiz, o badge fica empilhado no topo, liberando toda a largura para o texto
  const badgeWidthEst = hasBadge && !isRoot ? 48 : 0;
  const usableWidth = width - 28 - badgeWidthEst - (node.link ? 16 : 0);
  const charsPerLine = Math.max(14, Math.floor(usableWidth / 7.6));
  const lines = Math.max(1, Math.ceil(text.length / charsPerLine));

  if (isRoot) {
    return { width, height: Math.max(64, 28 + lines * 22 + (hasBadge ? 22 : 0)) };
  }
  // Altura base: 22px padding vertical + borda + 19px por linha de texto + badge
  const height = Math.max(46, 22 + lines * 19 + (hasBadge ? 4 : 0));
  return { width, height };
}

// Previne referências circulares em árvores hierárquicas
function isDescendant(nodes: Record<string, AstralNode>, ancestorId: string, targetId: string): boolean {
  let curr = nodes[targetId];
  while (curr && curr.parentId) {
    if (curr.parentId === ancestorId) return true;
    curr = nodes[curr.parentId];
  }
  return false;
}

export function AstralCanvas({
  mapData,
  onChangeMapData,
  readOnly = false,
  autoFitOnMount = false,
}: AstralCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const [pan, setPan] = useState(() => {
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    // Se for mobile, readOnly ou autoFitOnMount, não carrega coordenadas fixas de desktop
    if (!readOnly && !autoFitOnMount && !isMobile && mapData.viewport && (mapData.viewport.panX !== 0 || mapData.viewport.panY !== 0)) {
      return { x: mapData.viewport.panX, y: mapData.viewport.panY };
    }
    // Estimativa instantânea centralizada para mobile, modo somente leitura ou mapas novos
    if (typeof window !== "undefined") {
      const approxW = window.innerWidth;
      const approxH = Math.max(400, window.innerHeight - 56);
      return {
        x: Math.round(approxW / 2 - 110),
        y: Math.round(approxH / 2 - 26),
      };
    }
    return { x: 0, y: 0 };
  });
  const [zoom, setZoom] = useState(mapData.viewport?.zoom || 1);
  const [isPanning, setIsPanning] = useState(false);
  const [dragState, setDragState] = useState<DragState | null>(null);

  const panRef = useRef(pan);
  panRef.current = pan;
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const panStartRef = useRef({ x: 0, y: 0 });
  const rafPanRef = useRef<number | null>(null);
  const hasAutoCenteredRootIdRef = useRef<string | null>(null);

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(mapData.rootId);
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);

  const theme = ASTRAL_THEMES[mapData.theme] || ASTRAL_THEMES.dark;

  // Salva viewport alterado com debounce
  const updateViewportInMap = useCallback(
    (newPan: { x: number; y: number }, newZoom: number) => {
      if (readOnly || !onChangeMapData) return;
      onChangeMapData({
        ...mapData,
        viewport: {
          zoom: newZoom,
          panX: newPan.x,
          panY: newPan.y,
        },
      });
    },
    [mapData, onChangeMapData, readOnly]
  );

  // Árvore global de filhos e ordenação estável por nó
  const childrenMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    Object.values(mapData.nodes).forEach((n) => {
      if (n.parentId) {
        const list = map[n.parentId] ?? [];
        list.push(n.id);
        map[n.parentId] = list;
      }
    });
    Object.keys(map).forEach((parentId) => {
      map[parentId]?.sort((a, b) => {
        const orderA = mapData.nodes[a]?.order ?? 0;
        const orderB = mapData.nodes[b]?.order ?? 0;
        return orderA - orderB;
      });
    });
    return map;
  }, [mapData.nodes]);

  // -------------------------------------------------------------
  // MOTOR DE CÁLCULO DE COORDENADAS COM BARREIRA ANTI-COLISÃO
  // -------------------------------------------------------------
  const { positions, connections } = useMemo(() => {
    const nodes = mapData.nodes;
    const rootId = mapData.rootId;
    const rootNode = nodes[rootId];

    const posMap: Record<string, LayoutNodePosition> = {};
    const conns: { id: string; parentId: string; childId: string; x1: number; y1: number; x2: number; y2: number; color?: string }[] = [];

    if (!rootNode) return { positions: posMap, connections: conns };

    // Constantes de layout com margem de respiro e espaçamento confortável
    const HORIZONTAL_GAP = 85;
    const VERTICAL_GAP = 28;
    const SUBTREE_GAP = 36;
    const MIN_BREATHING_MARGIN = 26;

    // 1. Pré-calcula dimensões reais estimadas de cada nó
    const nodeDimensions: Record<string, { width: number; height: number }> = {};
    Object.values(nodes).forEach((n) => {
      nodeDimensions[n.id] = estimateNodeDimensions(n, n.id === rootId);
    });

    if (mapData.layout === "org") {
      // -------------------------------------------------------
      // LAYOUT ORGANOGRAMA (Top-Down Hierarchical Tree)
      // -------------------------------------------------------
      const subtreeWidths: Record<string, number> = {};

      const computeSubtreeWidth = (nodeId: string): number => {
        const node = nodes[nodeId];
        const myW = nodeDimensions[nodeId]?.width || 215;
        const children = !node?.isCollapsed && childrenMap[nodeId] ? childrenMap[nodeId] : [];
        if (children.length === 0) {
          subtreeWidths[nodeId] = myW;
          return myW;
        }
        let total = 0;
        children.forEach((cId, idx) => {
          total += computeSubtreeWidth(cId);
          if (idx < children.length - 1) total += 32;
        });
        subtreeWidths[nodeId] = Math.max(myW, total);
        return subtreeWidths[nodeId];
      };

      computeSubtreeWidth(rootId);

      const placeOrgNode = (nodeId: string, leftX: number, currentY: number) => {
        const myDims = nodeDimensions[nodeId] || { width: 215, height: 46 };
        const stWidth = subtreeWidths[nodeId] || myDims.width;
        const nodeX = leftX + (stWidth - myDims.width) / 2;

        posMap[nodeId] = {
          id: nodeId,
          x: nodeX,
          y: currentY,
          width: myDims.width,
          height: myDims.height,
          parentId: nodes[nodeId]?.parentId || null,
          side: "right",
        };

        const node = nodes[nodeId];
        const children = !node?.isCollapsed && childrenMap[nodeId] ? childrenMap[nodeId] : [];

        let childLeftX = leftX;
        children.forEach((childId) => {
          const childStWidth = subtreeWidths[childId] || 215;
          placeOrgNode(childId, childLeftX, currentY + myDims.height + 60);
          childLeftX += childStWidth + 32;
        });
      };

      placeOrgNode(rootId, 0, 0);
    } else if (mapData.layout === "list") {
      // -------------------------------------------------------
      // LAYOUT LISTA / OUTLINE HIERÁRQUICO (MindMeister Outline)
      // -------------------------------------------------------
      const rootDims = nodeDimensions[rootId] || { width: 220, height: 52 };
      const rootPos: LayoutNodePosition = {
        id: rootId,
        x: -240,
        y: -180,
        width: rootDims.width,
        height: rootDims.height,
        parentId: null,
        side: "right",
      };
      posMap[rootId] = rootPos;

      let currentY = -100;
      const INDENT_LEVEL_PX = 240;

      const placeListNode = (nodeId: string, level: number, parentId: string) => {
        const node = nodes[nodeId];
        if (!node) return;
        const myDims = nodeDimensions[nodeId] || { width: 215, height: 46 };

        const nodeX = rootPos.x + level * INDENT_LEVEL_PX;
        const nodeY = currentY;
        currentY += myDims.height + 16;

        posMap[nodeId] = {
          id: nodeId,
          x: nodeX,
          y: nodeY,
          width: myDims.width,
          height: myDims.height,
          parentId,
          side: "right",
        };

        const children = !node.isCollapsed && childrenMap[nodeId] ? childrenMap[nodeId] : [];
        children.forEach((childId) => {
          placeListNode(childId, level + 1, nodeId);
        });
      };

      const rootChildren = childrenMap[rootId] || [];
      rootChildren.forEach((childId) => {
        placeListNode(childId, 1, rootId);
      });
    } else {
      // -------------------------------------------------------
      // LAYOUT MAPA MENTAL RADIAL (Horizontal Branching)
      // -------------------------------------------------------
      const rootDims = nodeDimensions[rootId] || { width: 220, height: 52 };
      const rootPos: LayoutNodePosition = {
        id: rootId,
        x: 0,
        y: 0,
        width: rootDims.width,
        height: rootDims.height,
        parentId: null,
        side: "right",
      };
      posMap[rootId] = rootPos;

      // Separa ramos em Direita e Esquerda
      const rootChildren = childrenMap[rootId] || [];
      const rightChildren: string[] = [];
      const leftChildren: string[] = [];

      rootChildren.forEach((childId, idx) => {
        const n = nodes[childId];
        if (n?.side === "left" || (n?.side === undefined && idx % 2 === 1)) {
          leftChildren.push(childId);
        } else {
          rightChildren.push(childId);
        }
      });

      // Função recursiva com altura dinâmica real por subárvore
      const subtreeHeights: Record<string, number> = {};
      const computeSubtreeHeight = (nodeId: string): number => {
        const node = nodes[nodeId];
        const myDims = nodeDimensions[nodeId] || { width: 215, height: 46 };
        const children = !node?.isCollapsed && childrenMap[nodeId] ? childrenMap[nodeId] : [];
        if (children.length === 0) {
          subtreeHeights[nodeId] = myDims.height;
          return myDims.height;
        }
        let total = 0;
        children.forEach((cId, idx) => {
          total += computeSubtreeHeight(cId);
          if (idx < children.length - 1) total += VERTICAL_GAP;
        });
        subtreeHeights[nodeId] = Math.max(myDims.height, total);
        return subtreeHeights[nodeId];
      };

      [...rightChildren, ...leftChildren].forEach(computeSubtreeHeight);

      // Posiciona ramos laterais
      const placeSideBranch = (
        nodeList: string[],
        direction: "right" | "left",
        startX: number,
        centerY: number
      ) => {
        const totalHeight = nodeList.reduce((acc, cId, idx) => {
          return acc + (subtreeHeights[cId] || 46) + (idx > 0 ? SUBTREE_GAP : 0);
        }, 0);

        let currentY = centerY - totalHeight / 2;

        const layoutRecursive = (nodeId: string, currentX: number, startY: number, side: "right" | "left") => {
          const stHeight = subtreeHeights[nodeId] || 46;
          const myDims = nodeDimensions[nodeId] || { width: 215, height: 46 };
          const nodeY = startY + (stHeight - myDims.height) / 2;

          posMap[nodeId] = {
            id: nodeId,
            x: currentX,
            y: nodeY,
            width: myDims.width,
            height: myDims.height,
            parentId: nodes[nodeId]?.parentId || null,
            side,
          };

          const node = nodes[nodeId];
          const children = !node?.isCollapsed && childrenMap[nodeId] ? childrenMap[nodeId] : [];

          // Calcula altura acumulada total do bloco de filhos
          const totalChildrenHeight = children.reduce((acc, cId, idx) => {
            return acc + (subtreeHeights[cId] || 46) + (idx > 0 ? VERTICAL_GAP : 0);
          }, 0);

          // Centraliza o bloco de filhos diretamente em frente ao centro do card pai
          let childY = nodeY + myDims.height / 2 - totalChildrenHeight / 2;
          const nextX = side === "right"
            ? currentX + myDims.width + HORIZONTAL_GAP
            : currentX - (215 + HORIZONTAL_GAP);

          children.forEach((childId) => {
            const childHeight = subtreeHeights[childId] || 46;
            layoutRecursive(childId, nextX, childY, side);
            childY += childHeight + VERTICAL_GAP;
          });
        };

        nodeList.forEach((childId) => {
          const childStHeight = subtreeHeights[childId] || 46;
          layoutRecursive(childId, startX, currentY, direction);
          currentY += childStHeight + SUBTREE_GAP;
        });
      };

      // Posiciona ramo direito
      const rightStartX = rootPos.x + rootPos.width + HORIZONTAL_GAP;
      placeSideBranch(rightChildren, "right", rightStartX, rootPos.y + rootPos.height / 2);

      // Posiciona ramo esquerdo
      const leftStartX = rootPos.x - (215 + HORIZONTAL_GAP);
      placeSideBranch(leftChildren, "left", leftStartX, rootPos.y + rootPos.height / 2);

      // -------------------------------------------------------------
      // BARREIRA ANTI-COLISÃO (PASSAGEM DE RESPIRO OBRIGATÓRIA)
      // -------------------------------------------------------------
      const shiftSubtreeDown = (nodeId: string, deltaY: number) => {
        if (posMap[nodeId]) {
          posMap[nodeId].y += deltaY;
        }
        const kids = childrenMap[nodeId] || [];
        kids.forEach((kId) => shiftSubtreeDown(kId, deltaY));
      };

      const columns: Record<string, LayoutNodePosition[]> = {};
      Object.values(posMap).forEach((p) => {
        if (p.id === rootId) return;
        const colKey = `${p.side}_${Math.round(p.x / 40) * 40}`;
        if (!columns[colKey]) columns[colKey] = [];
        columns[colKey].push(p);
      });

      Object.values(columns).forEach((colNodes) => {
        colNodes.sort((a, b) => a.y - b.y);
        for (let i = 0; i < colNodes.length - 1; i++) {
          const current = colNodes[i];
          const next = colNodes[i + 1];
          if (!current || !next) continue;
          const minNextY = current.y + current.height + MIN_BREATHING_MARGIN;
          if (next.y < minNextY) {
            const overlap = minNextY - next.y;
            shiftSubtreeDown(next.id, overlap);
          }
        }
      });
    }

    // -------------------------------------------------------------
    // APLICAÇÃO DE OFFSETS MANUAIS LIVRES HIERÁRQUICOS (customX, customY)
    // -------------------------------------------------------------
    const getAccumulatedOffset = (nodeId: string) => {
      let offsetX = 0;
      let offsetY = 0;
      let curr: AstralNode | undefined = nodes[nodeId];
      while (curr) {
        if (typeof curr.customX === "number") offsetX += curr.customX;
        if (typeof curr.customY === "number") offsetY += curr.customY;
        curr = curr.parentId ? nodes[curr.parentId] : undefined;
      }
      return { offsetX, offsetY };
    };

    Object.values(posMap).forEach((p) => {
      const { offsetX, offsetY } = getAccumulatedOffset(p.id);
      p.x += offsetX;
      p.y += offsetY;
    });

    // -------------------------------------------------------------
    // CONSTRUÇÃO DAS LINHAS DE CONEXÃO BASEADAS NAS COORDENADAS REAIS
    // -------------------------------------------------------------
    Object.values(posMap).forEach((cPos) => {
      if (!cPos.parentId) return;
      const pPos = posMap[cPos.parentId];
      if (!pPos) return;

      if (mapData.layout === "org") {
        conns.push({
          id: `${cPos.parentId}-${cPos.id}`,
          parentId: cPos.parentId,
          childId: cPos.id,
          x1: pPos.x + pPos.width / 2,
          y1: pPos.y + pPos.height,
          x2: cPos.x + cPos.width / 2,
          y2: cPos.y,
          color: nodes[cPos.id]?.color || theme.lineColor,
        });
      } else if (mapData.layout === "list") {
        conns.push({
          id: `${cPos.parentId}-${cPos.id}`,
          parentId: cPos.parentId,
          childId: cPos.id,
          x1: pPos.x + 20,
          y1: pPos.y + pPos.height,
          x2: cPos.x,
          y2: cPos.y + cPos.height / 2,
          color: nodes[cPos.id]?.color || theme.lineColor,
        });
      } else {
        const isRight = cPos.side === "right";
        const x1 = isRight ? pPos.x + pPos.width : pPos.x;
        const y1 = pPos.y + pPos.height / 2;
        const x2 = isRight ? cPos.x : cPos.x + cPos.width;
        const y2 = cPos.y + cPos.height / 2;

        conns.push({
          id: `${cPos.parentId}-${cPos.id}`,
          parentId: cPos.parentId,
          childId: cPos.id,
          x1,
          y1,
          x2,
          y2,
          color: nodes[cPos.id]?.color || (cPos.parentId === rootId ? theme.accentColors[0] : theme.lineColor),
        });
      }
    });

    return { positions: posMap, connections: conns };
  }, [mapData.nodes, mapData.rootId, mapData.layout, theme, childrenMap]);

  // -------------------------------------------------------------
  // POSIÇÕES LIVE DURANTE O ARRASTO (COM MOVIMENTO DA SUBÁRVORE)
  // -------------------------------------------------------------
  const renderedPositions = useMemo(() => {
    if (!dragState) return positions;
    const base = { ...positions };
    const shiftSubtreeLive = (id: string) => {
      if (base[id]) {
        base[id] = {
          ...base[id],
          x: base[id].x + dragState.deltaX,
          y: base[id].y + dragState.deltaY,
        };
      }
      const children = childrenMap[id] || [];
      children.forEach(shiftSubtreeLive);
    };
    shiftSubtreeLive(dragState.nodeId);
    return base;
  }, [positions, dragState, childrenMap]);

  // -------------------------------------------------------------
  // CONTROLES DE ZOOM E PAN
  // -------------------------------------------------------------
  const handleZoom = useCallback(
    (delta: number, clientCenter?: { x: number; y: number }) => {
      setZoom((prevZoom) => {
        const newZoom = Math.min(2.5, Math.max(0.25, Number((prevZoom + delta).toFixed(2))));
        if (clientCenter && containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const mouseX = clientCenter.x - rect.left;
          const mouseY = clientCenter.y - rect.top;

          setPan((prevPan) => {
            const worldX = (mouseX - prevPan.x) / prevZoom;
            const worldY = (mouseY - prevPan.y) / prevZoom;
            const nextPanX = mouseX - worldX * newZoom;
            const nextPanY = mouseY - worldY * newZoom;
            updateViewportInMap({ x: nextPanX, y: nextPanY }, newZoom);
            return { x: nextPanX, y: nextPanY };
          });
        } else {
          updateViewportInMap(pan, newZoom);
        }
        return newZoom;
      });
    },
    [pan, updateViewportInMap]
  );

  // Wheel zoom centrado no mouse
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoomFactor = e.deltaY < 0 ? 0.08 : -0.08;
      handleZoom(zoomFactor, { x: e.clientX, y: e.clientY });
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
    };
  }, [handleZoom]);

  // Pan via arrasto do mouse no fundo do canvas
  const handleMouseDown = (e: RMouseEvent) => {
    if (e.button !== 0 || dragState !== null) return;
    const target = e.target as HTMLElement;

    if (
      target.closest("button") ||
      target.closest("input") ||
      target.closest("a") ||
      target.closest(".astral-floating-ctrl") ||
      target.closest("[data-astral-node]")
    ) {
      return;
    }

    setIsPanning(true);
    panStartRef.current = { x: e.clientX - panRef.current.x, y: e.clientY - panRef.current.y };
  };

  useEffect(() => {
    if (!isPanning) return;

    document.body.style.userSelect = "none";

    const onMouseMove = (e: MouseEvent) => {
      if (rafPanRef.current) cancelAnimationFrame(rafPanRef.current);
      rafPanRef.current = requestAnimationFrame(() => {
        const nextX = e.clientX - panStartRef.current.x;
        const nextY = e.clientY - panStartRef.current.y;
        setPan({ x: nextX, y: nextY });
      });
    };

    const onMouseUp = () => {
      if (rafPanRef.current) cancelAnimationFrame(rafPanRef.current);
      setIsPanning(false);
      document.body.style.userSelect = "";
      updateViewportInMap(panRef.current, zoomRef.current);
    };

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      if (rafPanRef.current) cancelAnimationFrame(rafPanRef.current);
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isPanning, updateViewportInMap]);

  // -------------------------------------------------------------
  // SISTEMA DE ARRASTO DE CARDS (REPARENTING, REORDER, ESPAÇAMENTO)
  // -------------------------------------------------------------
  const handleStartNodeDrag = useCallback(
    (nodeId: string, clientX: number, clientY: number) => {
      if (readOnly || nodeId === mapData.rootId) return; // Raiz permanece como âncora central
      const nodePos = positions[nodeId];
      if (!nodePos) return;

      setDragState({
        nodeId,
        startClientX: clientX,
        startClientY: clientY,
        startNodeX: nodePos.x,
        startNodeY: nodePos.y,
        currentNodeX: nodePos.x,
        currentNodeY: nodePos.y,
        deltaX: 0,
        deltaY: 0,
        hoverTargetId: null,
      });
    },
    [mapData.rootId, positions]
  );

  useEffect(() => {
    if (!dragState) return;

    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";

    const onMouseMove = (e: MouseEvent) => {
      const deltaX = (e.clientX - dragState.startClientX) / zoomRef.current;
      const deltaY = (e.clientY - dragState.startClientY) / zoomRef.current;
      const currX = dragState.startNodeX + deltaX;
      const currY = dragState.startNodeY + deltaY;

      const worldMouseX = (e.clientX - panRef.current.x) / zoomRef.current;
      const worldMouseY = (e.clientY - panRef.current.y) / zoomRef.current;

      // 1. Detecção de nó candidato a novo pai (Reparenting / Re-conexão)
      let foundHoverTarget: string | null = null;
      for (const pos of Object.values(positions)) {
        if (pos.id === dragState.nodeId) continue;
        if (isDescendant(mapData.nodes, dragState.nodeId, pos.id)) continue;

        if (
          worldMouseX >= pos.x - 15 &&
          worldMouseX <= pos.x + pos.width + 15 &&
          worldMouseY >= pos.y - 12 &&
          worldMouseY <= pos.y + pos.height + 12
        ) {
          foundHoverTarget = pos.id;
          break;
        }
      }

      setDragState((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          currentNodeX: currX,
          currentNodeY: currY,
          deltaX,
          deltaY,
          hoverTargetId: foundHoverTarget,
        };
      });
    };

    const onMouseUp = () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";

      if (dragState) {
        const { nodeId, hoverTargetId, deltaX, deltaY, currentNodeX } = dragState;
        const node = mapData.nodes[nodeId];

        if (node) {
          if (hoverTargetId) {
            // -------------------------------------------------------------
            // FUNÇÃO 2: REPARENTING (Tirar card de um lugar e conectar em outro)
            // -------------------------------------------------------------
            const targetNode = mapData.nodes[hoverTargetId];
            let newSide = targetNode?.side || "right";

            if (hoverTargetId === mapData.rootId) {
              const rootPos = positions[mapData.rootId];
              newSide = currentNodeX < (rootPos?.x || 0) ? "left" : "right";
            }

            const updatedNodes = { ...mapData.nodes };
            updatedNodes[nodeId] = {
              ...node,
              parentId: hoverTargetId,
              side: newSide,
              customX: 0,
              customY: 0,
            };

            const updateSides = (nId: string, s: "left" | "right") => {
              Object.values(updatedNodes).forEach((child) => {
                if (child.parentId === nId) {
                  updatedNodes[child.id] = { ...child, side: s };
                  updateSides(child.id, s);
                }
              });
            };
            updateSides(nodeId, newSide);

            onChangeMapData?.({
              ...mapData,
              nodes: updatedNodes,
            });
          } else if (Math.hypot(deltaX, deltaY) > 8) {
            // -------------------------------------------------------------
            // FUNÇÃO 1: ARRASTAR LIVREMENTE E SEPARAR COM RESPIRO
            // -------------------------------------------------------------
            const updatedNodes = { ...mapData.nodes };

            // Se for filho direto da raiz e cruzar o eixo central, troca de lado organicamente
            if (node.parentId === mapData.rootId) {
              const rootPos = positions[mapData.rootId];
              const rootCenterX = (rootPos?.x || 0) + (rootPos?.width || 220) / 2;

              if (currentNodeX < rootCenterX - 50 && node.side !== "left") {
                updatedNodes[nodeId] = { ...node, side: "left", customX: 0, customY: 0 };
                const updateSides = (nId: string) => {
                  Object.values(updatedNodes).forEach((child) => {
                    if (child.parentId === nId) {
                      updatedNodes[child.id] = { ...child, side: "left" };
                      updateSides(child.id);
                    }
                  });
                };
                updateSides(nodeId);
                onChangeMapData?.({ ...mapData, nodes: updatedNodes });
                setDragState(null);
                return;
              } else if (currentNodeX > rootCenterX + 50 && node.side !== "right") {
                updatedNodes[nodeId] = { ...node, side: "right", customX: 0, customY: 0 };
                const updateSides = (nId: string) => {
                  Object.values(updatedNodes).forEach((child) => {
                    if (child.parentId === nId) {
                      updatedNodes[child.id] = { ...child, side: "right" };
                      updateSides(child.id);
                    }
                  });
                };
                updateSides(nodeId);
                onChangeMapData?.({ ...mapData, nodes: updatedNodes });
                setDragState(null);
                return;
              }
            }

            // Salva deslocamento manual (respiro personalizado do card)
            const newCustomX = Math.round((node.customX || 0) + deltaX);
            const newCustomY = Math.round((node.customY || 0) + deltaY);

            updatedNodes[nodeId] = {
              ...node,
              customX: newCustomX,
              customY: newCustomY,
            };

            onChangeMapData?.({
              ...mapData,
              nodes: updatedNodes,
            });
          }
        }
      }

      setDragState(null);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragState, mapData, positions, onChangeMapData]);

  // Centralizar mapa / Fit to view com cálculo matemático preciso para mobile e desktop
  const handleFitToView = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const bbox = computeNodesBoundingBox(positions);
    const rootPos = positions[mapData.rootId];

    const { panX, panY, zoom: fitZoom } = computeFitViewport({
      containerWidth: rect.width,
      containerHeight: rect.height,
      bbox,
      rootPos,
    });

    setZoom(fitZoom);
    setPan({ x: panX, y: panY });
    updateViewportInMap({ x: panX, y: panY }, fitZoom);
  }, [positions, mapData.rootId, updateViewportInMap]);

  // Suporte avançado a gestos touch nativos (Pinch-to-zoom e Pan com inércia no mobile)
  useCanvasTouchGestures({
    containerRef,
    pan,
    zoom,
    setPan,
    setZoom,
    onViewportChangeEnd: (p, z) => updateViewportInMap(p, z),
    onDoubleTap: handleFitToView,
    readOnly,
  });

  const hasInitializedViewportRef = useRef<string | null>(null);

  // Sincroniza pan e zoom com o mapa quando carregado e auto-centraliza mapas novos, mobile ou com autoFitOnMount
  useEffect(() => {
    if (!mapData.rootId) return;

    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

    // Preserva a câmera do usuário local durante atualizações em tempo real recebidas de outros usuários
    if (hasInitializedViewportRef.current === mapData.rootId) {
      return;
    }
    hasInitializedViewportRef.current = mapData.rootId;

    const isDefaultViewport =
      !mapData.viewport ||
      (mapData.viewport.panX === 0 && mapData.viewport.panY === 0);

    if (isDefaultViewport || isMobile || autoFitOnMount || readOnly) {
      if (hasAutoCenteredRootIdRef.current !== mapData.rootId) {
        hasAutoCenteredRootIdRef.current = mapData.rootId;

        const attemptCenter = () => {
          if (!containerRef.current) return false;
          const rect = containerRef.current.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            handleFitToView();
            return true;
          }
          return false;
        };

        let raf: number | null = null;
        if (!attemptCenter()) {
          raf = requestAnimationFrame(() => {
            attemptCenter();
          });
        }
        return () => {
          if (raf) cancelAnimationFrame(raf);
        };
      }
    } else {
      setPan({ x: mapData.viewport.panX, y: mapData.viewport.panY });
      setZoom(mapData.viewport.zoom);
    }
    return undefined;
  }, [mapData.rootId, mapData.viewport, handleFitToView, autoFitOnMount, readOnly]);

  // -------------------------------------------------------------
  // OPERAÇÕES DE NÓS (TAB, ENTER, DELETE, EDIT)
  // -------------------------------------------------------------
  const handleAddChild = useCallback(
    (parentId: string) => {
      const newId = "node_" + Math.random().toString(36).substr(2, 9);
      const parentNode = mapData.nodes[parentId];
      const side = parentNode?.side || "right";

      const siblings = Object.values(mapData.nodes).filter((n) => n.parentId === parentId);
      const newOrder = siblings.length;

      const updatedNodes = {
        ...mapData.nodes,
        [newId]: {
          id: newId,
          parentId,
          text: "Novo Tópico",
          side,
          order: newOrder,
          isCollapsed: false,
        },
      };

      if (parentNode?.isCollapsed) {
        updatedNodes[parentId] = {
          ...parentNode,
          isCollapsed: false,
        };
      }

      onChangeMapData?.({
        ...mapData,
        nodes: updatedNodes,
      });

      setSelectedNodeId(newId);
      setEditingNodeId(newId);
    },
    [mapData, onChangeMapData]
  );

  const handleAddSibling = useCallback(
    (targetNodeId: string) => {
      const targetNode = mapData.nodes[targetNodeId];
      if (!targetNode || !targetNode.parentId) {
        handleAddChild(targetNodeId);
        return;
      }

      const parentId = targetNode.parentId;
      const newId = "node_" + Math.random().toString(36).substr(2, 9);
      const newOrder = (targetNode.order ?? 0) + 1;

      const updatedNodes = {
        ...mapData.nodes,
        [newId]: {
          id: newId,
          parentId,
          text: "Novo Tópico",
          side: targetNode.side || "right",
          order: newOrder,
          isCollapsed: false,
        },
      };

      onChangeMapData?.({
        ...mapData,
        nodes: updatedNodes,
      });

      setSelectedNodeId(newId);
      setEditingNodeId(newId);
    },
    [mapData, onChangeMapData, handleAddChild]
  );

  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      if (nodeId === mapData.rootId) return;

      const toDelete = new Set<string>();
      const collectChildren = (id: string) => {
        toDelete.add(id);
        Object.values(mapData.nodes).forEach((n) => {
          if (n.parentId === id) collectChildren(n.id);
        });
      };

      collectChildren(nodeId);

      const targetParentId = mapData.nodes[nodeId]?.parentId || mapData.rootId;

      const updatedNodes = { ...mapData.nodes };
      toDelete.forEach((id) => delete updatedNodes[id]);

      onChangeMapData?.({
        ...mapData,
        nodes: updatedNodes,
      });

      setSelectedNodeId(targetParentId);
      setEditingNodeId(null);
    },
    [mapData, onChangeMapData]
  );

  const handleSaveText = useCallback(
    (nodeId: string, newText: string) => {
      if (!mapData.nodes[nodeId]) return;

      const updatedNodes = {
        ...mapData.nodes,
        [nodeId]: {
          ...mapData.nodes[nodeId],
          text: newText,
        },
      };

      onChangeMapData?.({
        ...mapData,
        nodes: updatedNodes,
      });

      setEditingNodeId(null);
    },
    [mapData, onChangeMapData]
  );

  const handleToggleCollapse = useCallback(
    (nodeId: string) => {
      const node = mapData.nodes[nodeId];
      if (!node) return;

      const updatedNodes = {
        ...mapData.nodes,
        [nodeId]: {
          ...node,
          isCollapsed: !node.isCollapsed,
        },
      };

      onChangeMapData?.({
        ...mapData,
        nodes: updatedNodes,
      });
    },
    [mapData, onChangeMapData]
  );

  // Atalhos de teclado
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (readOnly || editingNodeId) return;

      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;

      if (!selectedNodeId) return;

      if (e.key === "Tab") {
        e.preventDefault();
        handleAddChild(selectedNodeId);
      } else if (e.key === "Enter") {
        e.preventDefault();
        handleAddSibling(selectedNodeId);
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        handleDeleteNode(selectedNodeId);
      } else if (e.key === "F2" || e.key === " ") {
        e.preventDefault();
        setEditingNodeId(selectedNodeId);
      } else if (e.key === "Escape") {
        setSelectedNodeId(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [selectedNodeId, editingNodeId, handleAddChild, handleAddSibling, handleDeleteNode]);

  // Contagem de filhos por nó
  const childCountMap = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.values(mapData.nodes).forEach((n) => {
      if (n.parentId) {
        counts[n.parentId] = (counts[n.parentId] || 0) + 1;
      }
    });
    return counts;
  }, [mapData.nodes]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        backgroundColor: theme.bgCanvas,
        backgroundImage: `radial-gradient(${theme.gridColor} 1.5px, transparent 1.5px)`,
        backgroundSize: "28px 28px",
        cursor: isPanning ? "grabbing" : dragState ? "grabbing" : "default",
      }}
      onMouseDown={handleMouseDown}
      onClick={() => {
        setSelectedNodeId(null);
        setEditingNodeId(null);
      }}
    >
      {/* Câmera / World Transform Container */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: "0 0",
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        {/* Camada SVG de Conexões Curvas de Bézier */}
        <svg
          style={{
            position: "absolute",
            left: "-10000px",
            top: "-10000px",
            width: "20000px",
            height: "20000px",
            pointerEvents: "none",
            overflow: "visible",
          }}
        >
          {connections.map((c) => {
            // Posicionamento dinâmico vinculado a renderedPositions para acompanhar arraste ao vivo
            const pPos = renderedPositions[c.parentId];
            const cPos = renderedPositions[c.childId];

            let targetX1 = c.x1;
            let targetY1 = c.y1;
            let targetX2 = c.x2;
            let targetY2 = c.y2;

            if (pPos && cPos && dragState) {
              if (mapData.layout === "org") {
                targetX1 = pPos.x + pPos.width / 2;
                targetY1 = pPos.y + pPos.height;
                targetX2 = cPos.x + cPos.width / 2;
                targetY2 = cPos.y;
              } else if (mapData.layout === "list") {
                targetX1 = pPos.x + 20;
                targetY1 = pPos.y + pPos.height;
                targetX2 = cPos.x;
                targetY2 = cPos.y + cPos.height / 2;
              } else {
                const isRight = cPos.side === "right";
                targetX1 = isRight ? pPos.x + pPos.width : pPos.x;
                targetY1 = pPos.y + pPos.height / 2;
                targetX2 = isRight ? cPos.x : cPos.x + cPos.width;
                targetY2 = cPos.y + cPos.height / 2;
              }
            }

            const svgX1 = targetX1 + 10000;
            const svgY1 = targetY1 + 10000;
            const svgX2 = targetX2 + 10000;
            const svgY2 = targetY2 + 10000;

            let pathD = "";
            if (mapData.layout === "list") {
              pathD = `M ${svgX1} ${svgY1} V ${svgY2} H ${svgX2}`;
            } else if (mapData.layout === "org") {
              const dy = Math.abs(svgY2 - svgY1) * 0.5;
              pathD = `M ${svgX1} ${svgY1} C ${svgX1} ${svgY1 + dy}, ${svgX2} ${svgY2 - dy}, ${svgX2} ${svgY2}`;
            } else {
              const dx = Math.abs(svgX2 - svgX1) * 0.55;
              pathD = `M ${svgX1} ${svgY1} C ${svgX1 + (svgX2 > svgX1 ? dx : -dx)} ${svgY1}, ${svgX2 + (svgX2 > svgX1 ? -dx : dx)} ${svgY2}, ${svgX2} ${svgY2}`;
            }

            return (
              <g key={c.id}>
                <path
                  d={pathD}
                  fill="none"
                  stroke={c.color || theme.lineColor}
                  strokeWidth="2"
                  strokeLinecap="round"
                  style={{ transition: "stroke 0.2s ease" }}
                />
                <circle cx={svgX1} cy={svgY1} r="3" fill={c.color || theme.lineColor} />
              </g>
            );
          })}

          {/* Curva de Pré-visualização de Reparenting quando pairando sobre novo pai */}
          {dragState && dragState.hoverTargetId && positions[dragState.hoverTargetId] && mapData.nodes[dragState.nodeId] && (
            (() => {
              const targetP = positions[dragState.hoverTargetId]!;
              const draggedNode = mapData.nodes[dragState.nodeId]!;
              const dDims = estimateNodeDimensions(draggedNode, false);
              const isRight = (targetP.side || "right") === "right";

              const x1 = (isRight ? targetP.x + targetP.width : targetP.x) + 10000;
              const y1 = targetP.y + targetP.height / 2 + 10000;
              const x2 = (isRight ? dragState.currentNodeX : dragState.currentNodeX + dDims.width) + 10000;
              const y2 = dragState.currentNodeY + dDims.height / 2 + 10000;

              const dx = Math.abs(x2 - x1) * 0.55;
              const previewD = `M ${x1} ${y1} C ${x1 + (x2 > x1 ? dx : -dx)} ${y1}, ${x2 + (x2 > x1 ? -dx : dx)} ${y2}, ${x2} ${y2}`;

              return (
                <g>
                  <path
                    d={previewD}
                    fill="none"
                    stroke="#6366f1"
                    strokeWidth="3"
                    strokeDasharray="6 4"
                    strokeLinecap="round"
                  />
                  <circle cx={x1} cy={y1} r="5" fill="#6366f1" />
                  <circle cx={x2} cy={y2} r="5" fill="#6366f1" />
                </g>
              );
            })()
          )}
        </svg>

        {/* Camada HTML de Cards / Tópicos */}
        <div style={{ position: "relative", pointerEvents: "auto" }}>
          {Object.values(renderedPositions).map((pos) => {
            const node = mapData.nodes[pos.id];
            if (!node) return null;

            return (
              <AstralNodeItem
                key={pos.id}
                node={node}
                isRoot={pos.id === mapData.rootId}
                isSelected={selectedNodeId === pos.id}
                isEditing={editingNodeId === pos.id}
                hasChildren={(childCountMap[pos.id] || 0) > 0}
                childCount={childCountMap[pos.id] || 0}
                theme={theme}
                defaultShape={mapData.defaultNodeShape}
                position={pos}
                isDragging={pos.id === dragState?.nodeId}
                isDropTarget={pos.id === dragState?.hoverTargetId}
                readOnly={readOnly}
                onSelect={(id) => setSelectedNodeId(id)}
                onStartEdit={(id) => setEditingNodeId(id)}
                onSaveText={handleSaveText}
                onCancelEdit={() => setEditingNodeId(null)}
                onAddChild={handleAddChild}
                onToggleCollapse={handleToggleCollapse}
                onStartDrag={handleStartNodeDrag}
              />
            );
          })}
        </div>
      </div>

      {/* ----------------------------------------------------------- */}
      {/* TOOLTIP FLUTUANTE DE ORIENTAÇÃO DE ARRASTO */}
      {/* ----------------------------------------------------------- */}
      {dragState && (
        <div
          style={{
            position: "absolute",
            bottom: "80px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "rgba(17, 24, 39, 0.95)",
            border: "1px solid rgba(99, 102, 241, 0.4)",
            borderRadius: "20px",
            padding: "8px 18px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#ffffff",
            fontSize: "12px",
            fontWeight: 600,
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.6), 0 0 20px rgba(99, 102, 241, 0.3)",
            zIndex: 90,
            pointerEvents: "none",
            animation: "pulse 1.8s infinite",
          }}
        >
          {dragState.hoverTargetId ? (
            <>
              <Sparkles size={15} style={{ color: "#a5b4fc" }} />
              <span>
                Solte para conectar como filho de <strong>{mapData.nodes[dragState.hoverTargetId]?.text}</strong>
              </span>
            </>
          ) : (
            <>
              <Move size={15} style={{ color: "#818cf8" }} />
              <span>Arraste para afastar com respiro ou solte sobre outro card para conectar</span>
            </>
          )}
        </div>
      )}

      {/* ----------------------------------------------------------- */}
      {/* CONTROLE FLUTUANTE DE ZOOM (Canto inferior direito MindMeister) */}
      {/* ----------------------------------------------------------- */}
      <div
        className="astral-floating-ctrl"
        style={{
          position: "absolute",
          bottom: "20px",
          right: "20px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          padding: "5px 10px",
          borderRadius: "12px",
          backgroundColor: "rgba(17, 22, 34, 0.92)",
          backdropFilter: "blur(16px)",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.6)",
          zIndex: 50,
          userSelect: "none",
        }}
      >
        <button
          type="button"
          onClick={() => handleZoom(-0.15)}
          style={{
            width: "34px",
            height: "34px",
            minWidth: "34px",
            borderRadius: "50%",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            color: "var(--ink-soft)",
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.12)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)")}
          title="Diminuir zoom"
        >
          <ZoomOut size={15} />
        </button>

        <button
          type="button"
          onClick={() => {
            setZoom(1);
            updateViewportInMap(pan, 1);
          }}
          style={{
            padding: "4px 8px",
            height: "34px",
            borderRadius: "17px",
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            fontSize: "11px",
            fontFamily: "var(--font-mono)",
            fontWeight: 600,
            color: "var(--ink)",
            cursor: "pointer",
            minWidth: "50px",
            textAlign: "center",
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.12)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)")}
          title="Clique para resetar em 100%"
        >
          {Math.round(zoom * 100)}%
        </button>

        <button
          type="button"
          onClick={() => handleZoom(0.15)}
          style={{
            width: "34px",
            height: "34px",
            minWidth: "34px",
            borderRadius: "50%",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            color: "var(--ink-soft)",
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.12)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)")}
          title="Aumentar zoom"
        >
          <ZoomIn size={15} />
        </button>

        <div style={{ width: "1px", height: "18px", backgroundColor: "rgba(255, 255, 255, 0.12)", margin: "0 2px" }} />

        <button
          type="button"
          onClick={handleFitToView}
          style={{
            width: "34px",
            height: "34px",
            minWidth: "34px",
            borderRadius: "50%",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            color: "var(--ink-soft)",
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.12)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)")}
          title="Centralizar e ajustar mapa na tela"
        >
          <Crosshair size={15} />
        </button>
      </div>

      {/* Dica de Ajuda Flutuante no Canto Inferior Esquerdo (Apenas em Desktop e Modo Edição) */}
      {!readOnly && (
        <div
          className="astral-floating-ctrl hidden md:flex"
          style={{
            position: "absolute",
            bottom: "20px",
            left: "20px",
            alignItems: "center",
            gap: "8px",
            padding: "6px 14px",
            borderRadius: "10px",
            backgroundColor: "rgba(17, 22, 34, 0.88)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            fontSize: "11px",
            color: "var(--ink-muted)",
            zIndex: 40,
            pointerEvents: "none",
          }}
        >
          <HelpCircle size={13} style={{ color: "var(--apple-blue)" }} />
          <span>Arraste cards para reorganizar ou conectar • <strong>Tab</strong> cria filho • <strong>Enter</strong> cria irmão • Roda do mouse dá zoom</span>
        </div>
      )}
    </div>
  );
}
